import type { Messages } from '../i18n/messages';

/**
 * Deploy Management page chrome. Per-component keys (board, matrix, drift
 * cells, diff tabs, ...) are added alongside each component as it's built.
 */
export const deploymentsMessages: Messages = {
  'deployments.page.title': { en: 'Deploy Management', fun: 'Ship Deck' },
  'deployments.page.subtitle': {
    en: 'Recent deploys and per-environment drift across all services',
    fun: 'Fresh drops and who’s lagging behind, across the fleet',
  },
  'deployments.page.helpLink': { en: 'Learn more', fun: 'Chart the course' },
  'deployments.matrix.title': { en: 'Services', fun: 'Fleet' },
  'deployments.matrix.errorTitle': {
    en: "Couldn't load services",
    fun: 'Lost at sea — the fleet manifest failed to load',
  },
  'deployments.matrix.retryButton': { en: 'Retry', fun: 'Try again' },
  'deployments.matrix.col.service': { en: 'Service', fun: 'Ship' },
  'deployments.matrix.col.serviceTooltip': {
    en: 'Catalog service name (links to its entity page).',
    fun: "Ship's name — jump straight to her full logbook.",
  },
  'deployments.matrix.col.dev': { en: 'Development', fun: 'Dry Dock' },
  'deployments.matrix.col.staging': { en: 'Staging', fun: 'Harbor' },
  'deployments.matrix.col.preprod': { en: 'Pre-prod', fun: 'Open Water' },
  'deployments.matrix.col.prod': { en: 'Production', fun: 'High Seas' },
  'deployments.matrix.col.environments': {
    en: 'Environments',
    fun: 'Ports of Call',
  },
  'deployments.matrix.col.environmentsTooltip': {
    en: 'Latest release tag deployed to each environment (development, staging, pre-prod, production), with its ArgoCD sync/health status.',
    fun: 'Latest tag anchored at every port of call — Dry Dock, Harbor, Open Water, High Seas — plus each one’s sync/health reading.',
  },
  'deployments.matrix.envDot.withStatus': {
    en: '{env} · {status}',
    fun: '{env} · {status}',
  },
  'deployments.matrix.envDot.notDeployed': {
    en: '{env} — not deployed',
    fun: '{env} — no cargo yet',
  },
  'deployments.matrix.col.diffStaging': {
    en: 'Diff (staging)',
    fun: 'Harbor drift',
  },
  'deployments.matrix.col.diffStagingTooltip': {
    en: 'Drift of staging vs its upstream (development): release tag, env vars, and secrets.',
    fun: 'How far Harbor has drifted from Dry Dock: tag, instruments, and buried treasure.',
  },
  'deployments.matrix.col.diffPreprod': {
    en: 'Diff (pre-prod)',
    fun: 'Open-water drift',
  },
  'deployments.matrix.col.diffPreprodTooltip': {
    en: 'Drift of pre-prod vs its upstream (staging).',
    fun: 'How far Open Water has drifted from Harbor.',
  },
  'deployments.matrix.col.diffProd': {
    en: 'Diff (production)',
    fun: 'High-seas drift',
  },
  'deployments.matrix.col.diffProdTooltip': {
    en: 'Drift of production vs its upstream (pre-prod).',
    fun: 'How far High Seas has drifted from Open Water.',
  },
  'deployments.matrix.col.actions': { en: 'Actions', fun: 'Orders' },
  'deployments.matrix.col.actionsTooltip': {
    en: 'Promote this service to the next environment in the chain.',
    fun: 'Scramble this ship to her next port of call.',
  },
  'deployments.matrix.quickPromote': { en: 'Promote', fun: 'Ship it' },
  'deployments.matrix.bulkSelectedCount': {
    en: '{count} selected',
    fun: '{count} in the boarding party',
  },
  'deployments.matrix.bulkPromote': {
    en: 'Promote {count}',
    fun: 'Ship {count} out',
  },
  'deployments.matrix.clearSelection': { en: 'Clear', fun: 'Stand down' },
  'deployments.matrix.searchPlaceholder': {
    en: 'Search services…',
    fun: 'Search the fleet…',
  },
  'deployments.matrix.selectAll': { en: 'Select all', fun: 'All hands' },
  'deployments.matrix.selectRow': {
    en: 'Select {service}',
    fun: 'Recruit {service}',
  },
  'deployments.matrix.empty': {
    en: 'No services match these filters',
    fun: 'No ships match this heading',
  },
  'deployments.matrix.filters.type': { en: 'Type', fun: 'Class' },
  'deployments.matrix.filters.allTypes': {
    en: 'All types',
    fun: 'All classes',
  },

  'deployments.matrix.filters.diffAria': {
    en: '{column} filter',
    fun: '{column} filter',
  },
  'deployments.matrix.filters.drift.envDiff': {
    en: 'Env vars differ',
    fun: 'Instruments differ',
  },
  'deployments.matrix.filters.drift.secretDiff': {
    en: 'Secrets differ',
    fun: 'Treasure differs',
  },
  'deployments.matrix.filters.drift.unknown': {
    en: 'Unknown',
    fun: 'Uncharted',
  },
  'deployments.matrix.filters.envShownAria': {
    en: 'Environments shown',
    fun: 'Ports shown',
  },
  'deployments.matrix.filters.envShownPlaceholder': {
    en: 'Environments',
    fun: 'Ports',
  },
  'deployments.matrix.filters.envArgoAria': {
    en: 'Environment sync/health filter',
    fun: 'Trim & tide filter',
  },
  'deployments.matrix.filters.envArgoPlaceholder': {
    en: 'Env status',
    fun: 'Trim',
  },
  'deployments.matrix.filters.envTagAria': {
    en: 'Environment tag filter',
    fun: 'Cargo tag filter',
  },
  'deployments.matrix.filters.envTagPlaceholder': {
    en: 'Tag…',
    fun: 'Cargo tag…',
  },

  'deployments.drift.inSync': { en: 'In sync', fun: 'Synced up' },
  'deployments.drift.notInSync': { en: 'Not in sync', fun: 'Drifted' },
  'deployments.drift.noUpstream': { en: 'No {env}', fun: 'No {env}' },
  'deployments.drift.tagBehind': { en: 'Tag behind', fun: 'Tag adrift' },
  'deployments.drift.tagBehindCount': {
    en: '{count} behind',
    fun: '{count} adrift',
  },
  'deployments.drift.envDiff': {
    en: '{count} env vars',
    fun: '{count} env adrift',
  },
  'deployments.drift.secretDiff': {
    en: '{count} secrets',
    fun: '{count} secrets adrift',
  },

  'deployments.pair.from': { en: 'From', fun: 'From' },
  'deployments.pair.to': { en: 'To', fun: 'To' },

  'deployments.diff.col.key': { en: 'Key', fun: 'Key' },
  'deployments.diff.col.status': { en: 'Status', fun: 'Status' },
  'deployments.diff.status.same': { en: 'Same', fun: 'Steady' },
  'deployments.diff.status.added': { en: 'Added', fun: 'New hire' },
  'deployments.diff.status.removed': { en: 'Removed', fun: 'Walked the plank' },
  'deployments.diff.status.changed': { en: 'Changed', fun: 'Adrift' },
  'deployments.diff.env.title': { en: 'Environment variables', fun: 'Ship instruments' },
  'deployments.diff.env.empty': {
    en: 'No environment variables to compare',
    fun: 'No instruments to compare',
  },
  'deployments.diff.env.searchAria': {
    en: 'Search environment variables…',
    fun: 'Search ship instruments…',
  },
  'deployments.diff.secret.title': { en: 'Secrets', fun: 'Buried treasure' },
  'deployments.diff.secret.empty': {
    en: 'No secrets to compare',
    fun: 'No treasure to compare',
  },
  'deployments.diff.secret.searchAria': {
    en: 'Search secrets…',
    fun: 'Search buried treasure…',
  },
  'deployments.diff.filters.statusAria': {
    en: 'Status filter',
    fun: 'Crew status filter',
  },
  'deployments.diff.filters.statusPlaceholder': {
    en: 'Status',
    fun: 'Crew status',
  },
  'deployments.diff.noMatch': {
    en: 'No rows match your filter',
    fun: 'No cargo matches your filter',
  },

  'deployments.release.title': { en: 'Releases', fun: 'Cargo manifest' },
  'deployments.release.promoted': { en: 'Promoted', fun: 'Delivered' },
  'deployments.release.behind': {
    en: '{env} is behind',
    fun: '{env} is lagging behind',
  },
  'deployments.release.notDeployed': {
    en: 'Not deployed',
    fun: 'No cargo yet',
  },
  'deployments.release.notApplicable': {
    en: "Release comparison isn't available for this environment pair.",
    fun: "No manifest can be charted for this pairing.",
  },
  'deployments.release.commitHint': { en: 'commit', fun: 'raw log entry' },
  'deployments.release.compareButton': {
    en: 'Compare on GitHub',
    fun: 'Chart the crossing on GitHub',
  },

  'deployments.commit.header': {
    en: '{ahead} commits in {from} not yet in {to}',
    fun: '{ahead} log entries departed {from}, waiting to dock at {to}',
  },
  'deployments.commit.searchPlaceholder': {
    en: 'Search by message or commit…',
    fun: "Search the ship's log by message or hash…",
  },
  'deployments.commit.inSync': {
    en: "In sync — no commits in {from} that aren't already in {to}",
    fun: "Smooth sailing — nothing logged in {from} that hasn't already docked at {to}",
  },
  'deployments.commit.compareFailed': {
    en: "Couldn't compare {from} and {to}",
    fun: "Couldn't chart a course between {from} and {to}",
  },
  'deployments.commit.noMatch': {
    en: 'No commits match your search',
    fun: 'No log entries match your search',
  },

  'deployments.detail.tab.release': { en: 'Release', fun: 'Cargo' },
  'deployments.detail.tab.git': { en: 'Git', fun: "Ship's Log" },
  'deployments.detail.tab.environment': {
    en: 'Environment',
    fun: 'Instruments',
  },
  'deployments.detail.tab.secret': { en: 'Secret', fun: 'Treasure' },

  'deployments.promote.successTitle': {
    en: 'Promote triggered',
    fun: 'Scramble ordered',
  },
  'deployments.promote.partialTitle': {
    en: 'Some promotions failed',
    fun: 'Some ships ran aground',
  },
  'deployments.promote.failedTitle': {
    en: 'Promote failed',
    fun: 'Promote capsized',
  },
  'deployments.promote.viewRun': {
    en: 'View run for {service}',
    fun: 'Watch {service} ship out',
  },
  'deployments.promote.failedRow': {
    en: '{service} failed: {error}',
    fun: '{service} ran aground: {error}',
  },

  'deployments.promoteDialog.singleTitle': {
    en: 'Promote {service}',
    fun: 'Set sail: {service}',
  },
  'deployments.promoteDialog.bulkTitle': {
    en: 'Promote {count} services',
    fun: 'Set sail: {count} ships',
  },
  'deployments.promoteDialog.currentTag': {
    en: 'Currently at {tag}',
    fun: 'Currently anchored at {tag}',
  },
  'deployments.promoteDialog.notYetDeployed': {
    en: 'Not yet deployed',
    fun: 'No cargo aboard yet',
  },
  'deployments.promoteDialog.confirming': {
    en: 'Promoting…',
    fun: 'Setting sail…',
  },
  'deployments.promoteDialog.targetLabel': {
    en: 'Target environment',
    fun: 'Destination',
  },
  'deployments.promoteDialog.approvalNote': {
    en: 'Promoting to {env} requires approval from the guard group before it runs.',
    fun: "Promoting to {env} needs the harbor master's sign-off (guard group approval) before it sails.",
  },
  'deployments.promoteDialog.confirm': { en: 'Promote', fun: 'Set sail' },
  'deployments.promoteDialog.helpLink': {
    en: 'Learn more',
    fun: 'Chart the course',
  },

  'deployments.ticket.deployedEnv': { en: 'Deployed {env}', fun: 'Docked {env}' },
  'deployments.ticket.allEnvs': {
    en: 'All environments deployed',
    fun: 'Docked everywhere',
  },
  'deployments.ticket.notDeployed': { en: 'Not deployed', fun: 'Not shipped' },
  'deployments.ticket.commitCount': { en: '{count} commits', fun: '{count} commits' },
  'deployments.ticket.notFound': {
    en: 'Not found in any service: {tickets}',
    fun: 'No sign of: {tickets}',
  },
  'deployments.ticket.empty': {
    en: 'No deployed commits found for these tickets',
    fun: 'No cargo found for these tickets',
  },
  'deployments.ticket.rateLimited': {
    en: 'GitHub search is rate-limited right now — some results may be missing. Try again shortly.',
    fun: 'The lookout is winded — some results may be missing. Try again shortly.',
  },
  'deployments.ticket.errorTitle': {
    en: "Couldn't run the lookup",
    fun: "Couldn't run the lookup",
  },
  'deployments.ticket.title': { en: 'Find ticket', fun: 'Track cargo' },
  'deployments.ticket.button': { en: 'Find ticket', fun: 'Track cargo' },
  'deployments.ticket.ticketsPlaceholder': {
    en: 'REG-1234, REG-5678…',
    fun: 'REG-1234, REG-5678…',
  },
  'deployments.ticket.ticketsHint': {
    en: 'Press Enter after each ticket to add it as a chip (or paste several).',
    fun: 'Press Enter after each ticket to tag it (or paste a batch).',
  },
  'deployments.ticket.run': { en: 'Run lookup', fun: 'Track it' },
  'deployments.ticket.envAll': { en: 'All environments', fun: 'All ports' },
};
